import React, { useCallback, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Tree, Icon, Button, Table, Row, Switch, Typography, Tag, Col, Empty, Form, Input, Modal,
} from 'antd';
import ConfigHeading from '../components/ConfigHeading';
import { filterPublished } from './config-util';
import routeLocations, { configRoutes } from '../../../../utility/route-locations';
import {
  selectChecklistsPageDataRequest, selectLoadingActiveStatus, selectAllChecklists,
  selectAllChecklistsConfig, selectAddChecklistRequest, selectAddChecklistState,
} from '../../../../state/ui/configuration/inspection/checklistConfigUiReducer';
import {
  beginChecklistAddRequest, cancelChecklistAddRequest, requestAddChecklist,
  requestChecklistsPageData, requestPublishChecklist,
} from '../../../../state/ui/configuration/inspection/checklistConfigUiActions';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from '../company/user-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';

const { TreeNode } = Tree;
const { Column } = Table;
const { Text } = Typography;

/**
 * Popup to amend the title/description
 */
const CreateDescriptionModal = injectIntl(({
  form, visible, titleId, descValue, descValidator, saveFunction, cancelFunction, buttonIdSave, buttonIdCancel, isLoading, validationBox,
}) => (
  <Modal
    title={<FormattedMessage id={titleId} />}
    okText={<FormattedMessage id={buttonIdSave} />}
    onOk={saveFunction}
    visible={visible}
    cancelText={<FormattedMessage id={buttonIdCancel} />}
    onCancel={cancelFunction}
    confirmLoading={isLoading}
    destroyOnClose
  >
    <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); saveFunction(); }}>
      <ViaValidationAlert validationError={validationBox} />
      <DescriptionField form={form} descValue={descValue} checkForDuplicates={descValidator} />
    </Form>
  </Modal>
));

/**
 * The field for the description label.  Allows clear, and max length set.  Required plus extra duplicate validation.
 * The rules are injected into the form using the form field decorator.
 */
const DescriptionField = injectIntl(({
  form, intl, descValue, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="description" />}>
    { form.getFieldDecorator('description', {
      initialValue: descValue,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (checkForDuplicates(value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * Gives more detail on answer options for a checklist question (exported)
 */
export const ViaChecklistTags = ({
  dataType, digits, show, active, showNotPublishedTag,
}) => {
  let showBoolean = false;
  let showNA = false;
  let showNumber = false;
  let showNotPublished = true;

  if (show) {
    if (dataType === 'BOOLEAN' || dataType === 'BOOLEAN_NA') { showBoolean = true; }
    if (dataType === 'BOOLEAN_NA' || dataType === 'INTEGER_NA') { showNA = true; }
    if (dataType === 'INTEGER' || dataType === 'INTEGER_NA') { showNumber = true; }
  }

  let numberText = '';
  switch (digits) {
    case 1:
      numberText = '0-9';
      break;
    case 2:
      numberText = '0-99';
      break;
    case 3:
      numberText = '0-999';
      break;
    default:
      numberText = '';
      break;
  }

  showNotPublished = showNotPublishedTag && !active;

  return (
    <Row type="flex" align="middle">
      <Tag
        color="volcano"
        style={{
          marginRight: show ? '0.5em' : 0, marginTop: '0.1em', marginBottom: '0.1em', display: showNotPublished ? '' : 'none',
        }}
      >
        <FormattedMessage id="notPublished" />
      </Tag>
      <Tag className="via-checklist-tag" color="#3399ff" style={{ display: showNA ? '' : 'none' }}><FormattedMessage id="NA" /></Tag>
      <Tag className="via-checklist-tag" color="#e68a00" style={{ display: showBoolean ? '' : 'none' }}><FormattedMessage id="fail" /></Tag>
      <Tag className="via-checklist-tag" color="#00b33c" style={{ display: showBoolean ? '' : 'none' }}><FormattedMessage id="pass" /></Tag>
      <Tag className="via-checklist-tag" color="#5c5c8a" style={{ display: showNumber ? '' : 'none' }}>{numberText}</Tag>
    </Row>
  );
};
ViaChecklistTags.propTypes = {
  dataType: PropTypes.string,
  digits: PropTypes.number,
  show: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  showNotPublishedTag: PropTypes.bool,
};

ViaChecklistTags.defaultProps = {
  dataType: '',
  digits: 0,
  showNotPublishedTag: true,
};

/**
 * Used to style and add detail to the read only tree nodes
 */
const ViaTreeTitle = ({
  title, icon, bold, dataType, digits, showTags, active,
}) => (
  <Row type="flex" align="middle" justify="space-between" className={icon ? 'via-checklist-node-item' : 'via-checklist-node-category'}>
    <Col span={16}>
      <Row type="flex" align="middle">
        <Col style={{ width: icon ? '25px' : '0' }}>
          <Row type="flex" align="middle">
            <Icon style={{ display: icon ? '' : 'none' }} className="via-checklist-icon" type={icon || 'plus'} />
          </Row>
        </Col>
        <Col style={{ width: icon ? 'calc(100% - 25px)' : '100%' }}>
          <Text style={{ fontWeight: bold ? 'bold' : '', whiteSpace: 'pre-wrap' }}>{title}</Text>
        </Col>
      </Row>
    </Col>
    <Col span={8}>
      <Row type="flex" align="middle" justify="end">
        <ViaChecklistTags dataType={dataType} digits={digits} show={showTags} active={active} />
      </Row>
    </Col>
  </Row>
);
ViaTreeTitle.propTypes = {
  bold: PropTypes.bool,
  dataType: PropTypes.string,
  digits: PropTypes.number,
  icon: PropTypes.string,
  showTags: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
};
ViaTreeTitle.defaultProps = {
  bold: false,
  dataType: '',
  digits: 0,
  icon: '',
};

/**
 * Read-only view of checklist
 */
export const ViaChecklistView = ({ checklistConfig }) => (
  <Row>
    <Col xs={0} sm={0} md={3} lg={3} xl={3} />
    <Col xs={24} sm={24} md={21} lg={21} xl={21}>
      {
          checklistConfig.checkListCategories.length < 1
            ? (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<FormattedMessage id="incompleteChecklist" />} />)
            : (
              <Tree
                className="via-checklist"
                showIcon
                defaultExpandAll
                defaultSelectedKeys={[]}
                selectable={false}
                switcherIcon={<Icon className="via-config-expand-switcher" type="caret-down" />}
              >
                {
                  checklistConfig.checkListCategories.map((category) => {
                    const catKey = `${checklistConfig.checkListCode}-${category.categoryCode}`;
                    return (
                      <TreeNode
                        title={(
                          <ViaTreeTitle
                            title={category.categoryDescription}
                            showTags={false}
                            active={category.active}
                          />
                        )}
                        key={catKey}
                      >
                        {
                          category.checkListItems.map((item) => {
                            const itemKey = `${checklistConfig.checkListCode}-${category.categoryCode}-${item.itemCode}`;
                            return (
                              <TreeNode
                                title={(
                                  <ViaTreeTitle
                                    title={item.itemDescription}
                                    icon="question-circle"
                                    showTags
                                    dataType={item.itemDataType}
                                    digits={item.itemDigitCount}
                                    active={item.active}
                                  />
                                )}
                                key={itemKey}
                              />
                            );
                          })
                        }
                      </TreeNode>
                    );
                  })
                }
              </Tree>
            )
        }
    </Col>
  </Row>
);
ViaChecklistView.propTypes = {
  checklistConfig: PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  }),
};
ViaChecklistView.defaultProps = {
  checklistConfig: null,
};

/**
 * Main Checklists Component
 */
function Checklists({
  isLoading, loadPageData, checklists, checklistsConfig, loadingActive,
  updateActiveStatus, startChecklistAdd, addNewChecklist, cancelChecklistAdd, sessionUser,
  createLoading, createValBox, addPopoverState, form,
}) {
  const history = useHistory();

  // Load checklist data on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Extra validation helper to get unique new description for dummy new checklist
  const doesDescriptionExist = value => checklists.some(row => row.description.toString().toLowerCase() === (value || '').toString().toLowerCase());

  // TITLE ROW & CHECKLIST CREATE
  const goToNewChecklist = useCallback((checklistId) => {
    // Go to Edit page
    history.push(`${configRoutes.configure_checklists}/${checklistId}`);
  }, [history]);

  const modalSaveFunction = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {
        // Create new checklist in DB
        const newChecklist = { description: values.description, active: false };
        addNewChecklist(newChecklist, goToNewChecklist);
      }
    });
  }, [form, addNewChecklist, goToNewChecklist]);

  const CreateButton = () => (
    <Button type="primary" onClick={() => startChecklistAdd()} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // TABLE
  const isActiveLoading = useCallback(reason => !!loadingActive.find(code => `${code}` === `${reason.reasonCode}`), [loadingActive]);

  const RowTotal = () => (
    <div>
      <Text>{`${checklists.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // SORTS
  function sortChecklistsByDesc(a, b) {
    const aStr = `${a.description}`;
    const bStr = `${b.description}`;
    return aStr.localeCompare(bStr);
  }

  return (
    <ConfigHeading
      titleId="checklists"
      descriptionId="manageChecklistsFunction"
      actions={(userIsAdmin(sessionUser) ? <CreateButton /> : <></>)}
      links={[(
        <Link to={routeLocations.configure_checklist_categories}>
          <FormattedMessage id="checklistCategories" />
        </Link>
      ), (
        <Link to={routeLocations.configure_checklist_items}>
          <FormattedMessage id="checklistQuestions" />
        </Link>
      )]}
    >
      <Table
        pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
        scroll={{ x: 1, y: false }}
        dataSource={checklists}
        rowKey={checklist => checklist.checkListCode}
        loading={isLoading}
        expandIcon={({ expanded, record, onExpand }) => (
          <Icon
            className="via-config-expand-icon"
            theme="filled"
            type={expanded ? 'caret-down' : 'caret-right'}
            onClick={e => onExpand(record, e)}
          />
        )}
        expandIconAsCell={false}
        expandIconColumnIndex={1}
        expandedRowRender={(record) => {
          const checklistConfig = checklistsConfig.find(configRow => configRow.checkListCode === record.checkListCode);
          return <ViaChecklistView checklistConfig={checklistConfig} />;
        }}
      >
        <Column
          title={<FormattedMessage id="published" />}
          dataIndex="active"
          key="active"
          filtered
          filters={[{ text: <FormattedMessage id="published" />, value: true }, { text: <FormattedMessage id="notPublished" />, value: false }]}
          filteredValue={checklists.active}
          onFilter={filterPublished}
          width={110}
          render={(active, record) => (
            <Row type="flex" justify="center">
              { userIsAdmin(sessionUser)
                ? <Switch loading={isActiveLoading(record)} onChange={() => updateActiveStatus(record)} size="small" checked={active} />
                : <Tag color={active ? 'blue' : 'volcano'}><FormattedMessage id={active ? 'published' : 'notPublished'} /></Tag> }
            </Row>
          )}
        />
        <Column
          sorter={sortChecklistsByDesc}
          defaultSortOrder="ascend"
          title={<FormattedMessage id="description" />}
          dataIndex="description"
        />
        { userIsAdmin(sessionUser)
          ? (
            <Column
              className="via-action-column"
              align="center"
              width={100}
              render={(text, record) => (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => history.push(`${configRoutes.configure_checklists}/${record.checkListCode}`)}
                  icon="edit"
                >
                  <FormattedMessage id="edit" />
                </Button>
              )}
            />
          )
          : ''}
      </Table>
      <CreateDescriptionModal
        form={form}
        visible={addPopoverState.popOverVisible}
        titleId="createNewChecklist"
        descValue={addPopoverState.description}
        descValidator={doesDescriptionExist}
        saveFunction={modalSaveFunction}
        cancelFunction={cancelChecklistAdd}
        buttonIdSave="save"
        buttonIdCancel="cancel"
        isLoading={createLoading}
        validationBox={createValBox}
      />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
Checklists.propTypes = {
  isLoading: PropTypes.bool,
  createLoading: PropTypes.bool,
  createValBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
  loadPageData: PropTypes.func.isRequired,
  updateActiveStatus: PropTypes.func.isRequired,
  checklists: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  checklistsConfig: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
    checkListCategories: PropTypes.arrayOf(PropTypes.object),
  })),
  loadingActive: PropTypes.arrayOf(PropTypes.string),
  addNewChecklist: PropTypes.func.isRequired,
  startChecklistAdd: PropTypes.func.isRequired,
  cancelChecklistAdd: PropTypes.func.isRequired,
  addPopoverState: PropTypes.shape({
    description: PropTypes.string,
    popOverVisible: PropTypes.bool,
  }).isRequired,
  sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
  form: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

Checklists.defaultProps = {
  isLoading: false,
  createLoading: false,
  createValBox: null,
  checklists: [],
  checklistsConfig: [],
  loadingActive: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectChecklistsPageDataRequest(state);
  const addRequest = selectAddChecklistRequest(state);
  const checklists = selectAllChecklists(state);
  const checklistsConfig = selectAllChecklistsConfig(state);
  const addState = selectAddChecklistState(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    createLoading: addRequest ? addRequest.isLoading : false,
    loadingActive: selectLoadingActiveStatus(state),
    error: pageDataRequest ? pageDataRequest.error : null,
    createValBox: addRequest ? addRequest.error : null,
    checklists: checklists || [],
    checklistsConfig: checklistsConfig || [],
    addPopoverState: addState,
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestChecklistsPageData()),
  updateActiveStatus: checklist => dispatch(requestPublishChecklist(checklist)),
  addNewChecklist: (checklist, successFunction) => dispatch(requestAddChecklist(checklist, successFunction)),
  startChecklistAdd: () => dispatch(beginChecklistAddRequest()),
  cancelChecklistAdd: () => dispatch(cancelChecklistAddRequest()),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(Form.create({ name: 'new-checklist' })(Checklists));
